import { useState, useEffect } from "react";
import { smsParts, SMS_PART, SMS_TOTAL_MAX } from "./smsParts";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { JourneyLine } from "./JourneyLine";
import { Dog, FileText, Mail, PauseCircle, Save, ExternalLink, Loader2, Check, X, Send, Phone } from "lucide-react";

const MAX = SMS_TOTAL_MAX;
export const chars = (t) => [...(t || "")].length;
export const digits = (p) => (p || "").replace(/\D/g, "");

// null = can be texted now; otherwise why not.
export const blockReason = (d, phone) => !d ? "loading" : d.error ? "error" : d.skip ? "skip" : d.paused ? "paused"
  : !d.sms_ok ? "no_sms" : !d.can_send_via_gingr && digits(phone ?? d.phone).length < 10 ? "no_phone" : null;

const Chip = ({ ok, label, tid, title }) => (
  <span title={title} data-testid={tid}
    className={`inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full border px-2 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
    {label} {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
  </span>
);

// One lead in the Follow-Up Queue "Text drafts" workspace: SMS/Email readiness, draft, per-lead send.
export const LeadDraftCard = ({ lead, d, checked, onCheck, onText, onPhone, phone, onSave, onOpen, onSend, sending }) => {
  const [text, setText] = useState(d?.text || "");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setText(d?.text || ""); }, [d?.text]);
  useEffect(() => { onText(lead.id, text); }, [text, lead.id, onText]);
  const block = blockReason(d, phone);
  const n = chars(text);
  const dirty = d && text.trim() !== (d.text || "").trim();
  const noGingr = d && !d.can_send_via_gingr;
  const smsTitle = d?.sms_why || "";
  return (
    <div className={`bg-white border rounded-2xl p-4 shadow-sm space-y-3 transition-colors ${checked ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`} data-testid={`lead-draft-${lead.id}`}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <Checkbox className="mt-1" checked={checked} disabled={!!block} onCheckedChange={onCheck} data-testid={`lead-draft-select-${lead.id}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-900">{lead.name || "Unknown"}</span>
            {lead.self_registered && <span data-testid={`lead-draft-selfreg-${lead.id}`}><SelfRegisteredBadge size="sm" /></span>}
            {d?.reviewed_at && <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5" data-testid={`lead-draft-reviewed-${lead.id}`}>✓ Reviewed{d.reviewed_by ? ` by ${d.reviewed_by}` : ""}{d.edited ? " · edited" : ""}{d.reviewed_channel === "email" ? " · email" : ""}</span>}
            {lead.pet_name && <span className="inline-flex items-center gap-1 text-sm text-amber-700 font-semibold"><Dog className="w-4 h-4" />{lead.pet_name}</span>}
            <span className="text-xs text-slate-400">Age {lead.days_since_created ?? 0}d · {lead.outreach_count ?? 0} contacts</span>
            <a href={`/leads/${lead.id}?from=queue`} target="_blank" rel="noreferrer" data-testid={`lead-draft-view-${lead.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline">
              <FileText className="w-3.5 h-3.5" />View lead details
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {lead.service && <span className="text-xs text-purple-700 mr-1">{lead.service}</span>}
            {d && !d.error && <Chip ok={d.sms_ok} label="SMS" tid={`lead-draft-sms-${lead.id}`} title={smsTitle} />}
            {d && !d.error && <Chip ok={d.email_ok} label="Email" tid={`lead-draft-email-${lead.id}`} title={d.email || "No email on file"} />}
            {d && !d.error && <span className="text-[11px] text-slate-500" data-testid={`lead-draft-sms-why-${lead.id}`}>· {d.sms_source === "gingr" ? "Gingr SMS flag" : "web form opt-in"}{noGingr && d.sms_ok ? " — not in Gingr, invites them to sign up" : ""}</span>}
            {d?.paused && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700"><PauseCircle className="w-3 h-3" />Replied — paused</span>}
          </div>
          {d && !d.error && <div className="mt-1"><JourneyLine j={d.journey} tid={`lead-draft-journey-${lead.id}`} /></div>}
        </div>
      </div>
      {!d ? (
        <div className="flex items-center gap-2 text-xs text-slate-400" data-testid={`lead-draft-loading-${lead.id}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Writing draft…</div>
      ) : d.error ? (
        <div className="text-xs text-rose-600">Draft failed: {d.error}</div>
      ) : d.skip ? (
        <div className="text-sm text-slate-500" data-testid={`lead-draft-skip-${lead.id}`}>{d.journey?.action === "remove" ? "Already booked in Gingr — marked Booked and removed from the queue." : "No follow-up needed — Gingr sends their tour reminders."}</div>
      ) : !d.sms_ok ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Mail className="w-4 h-4 text-sky-600" /> {d.sms_why}{d.email_ok ? " — reach them by email." : " — no email on file, call them."}
          <Button size="sm" className="ml-auto bg-sky-600 hover:bg-sky-700" onClick={() => onOpen(lead.id)} data-testid={`lead-draft-open-${lead.id}`}>
            {d.email_ok ? <><Mail className="w-3.5 h-3.5 mr-1" />Write email</> : <><ExternalLink className="w-3.5 h-3.5 mr-1" />Open</>}
          </Button>
        </div>
      ) : (
        <>
          {noGingr && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              <Input value={phone ?? d.phone ?? ""} onChange={(e) => onPhone(lead.id, e.target.value)} placeholder="Mobile number" className="h-8 w-48 text-sm" data-testid={`lead-draft-phone-${lead.id}`} />
            </div>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className={n > MAX ? "border-rose-400" : ""} data-testid={`lead-draft-text-${lead.id}`} />
          {n > SMS_PART && n <= MAX && (
            <div className="space-y-1" data-testid={`lead-draft-parts-${lead.id}`}>
              {smsParts(text).map((p, i) => (
                <div key={i} className="text-xs text-slate-600 bg-[#F5F9F9] border border-[#1F8A7C]/15 rounded p-2 whitespace-pre-wrap">
                  <span className="font-mono font-bold text-[#1F8A7C] mr-1">Text {i + 1}/2 · {p.length}</span>{p}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold font-mono ${n > MAX ? "text-rose-600" : n > SMS_PART ? "text-amber-600" : "text-slate-400"}`} data-testid={`lead-draft-count-${lead.id}`}>{n}/{n > SMS_PART ? MAX : SMS_PART}{n > SMS_PART && n <= MAX ? " · sends as 2 texts" : ""}</span>
            {d.edited && <span className="text-[11px] text-slate-400">edited</span>}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={!dirty || n > MAX || !text.trim()} onClick={() => onSave(lead.id, text.trim())} data-testid={`lead-draft-save-${lead.id}`}>
                <Save className="w-3.5 h-3.5 mr-1" />Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => onOpen(lead.id)} data-testid={`lead-draft-open-${lead.id}`}>
                {d.email_ok ? <><Mail className="w-3.5 h-3.5 mr-1" />Email</> : <><ExternalLink className="w-3.5 h-3.5 mr-1" />Open</>}
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={!!block || n > MAX || !text.trim() || sending}
                onClick={() => setConfirm(true)} data-testid={`lead-draft-send-${lead.id}`}>
                <Send className="w-3.5 h-3.5 mr-1" />Send via Gingr
              </Button>
            </div>
          </div>
        </>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`lead-draft-confirm-${lead.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Text {lead.name || "this lead"} via Gingr{noGingr ? ` at ${phone ?? d?.phone}` : ""}?</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap">{text.trim()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onSend} data-testid={`lead-draft-confirm-send-${lead.id}`}>Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
